/**
 * Music-theory primitives shared by the chord/circle components.
 *
 * Spelling is delegated to `theory/spell.ts` — nothing in this file indexes a
 * twelve-name table with a pitch class, because a pitch class cannot tell D♯
 * from E♭. Names here are ASCII ("Bb", "F#") so they can be handed straight to
 * Tone.js and VexFlow; use `pretty()` for anything shown to a student.
 */

import { spellDegree, parseNote, toMidi, accidental } from "@/theory/spell";

export type Quality = "major" | "minor" | "diminished" | "augmented";

/** The degree recipe for each triad quality. */
const TRIAD_DEGREES: Record<Quality, string[]> = {
  major:      ["1", "3",  "5"],
  minor:      ["1", "b3", "5"],
  diminished: ["1", "b3", "b5"],
  augmented:  ["1", "3",  "#5"],
};

/** Turn ASCII accidentals into the ♯/♭ glyphs used everywhere on screen. */
export function pretty(name: string): string {
  return name.replace(/##/g, "𝄪").replace(/#/g, "♯").replace(/bb/g, "𝄫").replace(/b/g, "♭");
}

export function pcOf(name: string): number {
  const p = parseNote(name);
  if (!p) throw new Error(`Unknown pitch class: ${name}`);
  return p.pc;
}

/**
 * Build a triad on a root, spelled correctly.
 *   triadPcs("Db", "major") → ["Db", "F", "Ab"]   (not C#, F, G#)
 *   triadPcs("E",  "augmented") → ["E", "G#", "B#"]  (not C)
 */
export function triadPcs(root: string, q: Quality): string[] {
  return TRIAD_DEGREES[q].map((d) => spellDegree(root, d, true) ?? root);
}

/**
 * Octavise spelled pitch classes into an ascending voicing.
 * The octave is chosen from the *sounding* pitch, so B♯ and C♭ land in the
 * register a musician expects rather than the one their letter implies.
 */
export function voicing(pcs: string[], startOctave = 4): string[] {
  let lastMidi = -Infinity;
  let octave = startOctave;
  const out: string[] = [];
  for (const pc of pcs) {
    let midi = toMidi(pc, octave);
    if (midi == null) { out.push(`${pc}${octave}`); continue; }
    while (midi <= lastMidi) {
      octave++;
      midi = toMidi(pc, octave)!;
    }
    out.push(`${pc}${octave}`);
    lastMidi = midi;
  }
  return out;
}

/** Major scale degrees as roman-numeral chord qualities. */
export const MAJOR_DIATONIC: { degree: string; quality: Quality }[] = [
  { degree: "I",   quality: "major" },
  { degree: "ii",  quality: "minor" },
  { degree: "iii", quality: "minor" },
  { degree: "IV",  quality: "major" },
  { degree: "V",   quality: "major" },
  { degree: "vi",  quality: "minor" },
  { degree: "vii°",quality: "diminished" },
];

/** Major scale of a tonic, spelled with every letter exactly once. */
export function majorScalePcs(tonic: string): string[] {
  return ["1", "2", "3", "4", "5", "6", "7"].map((d) => spellDegree(tonic, d, true) ?? tonic);
}

/** Diatonic triads of a major key, in scale-degree order, correctly spelled. */
export function diatonicTriads(tonic: string): { roman: string; pcs: string[]; quality: Quality }[] {
  const scale = majorScalePcs(tonic);
  return MAJOR_DIATONIC.map((d, i) => ({
    roman: d.degree,
    quality: d.quality,
    pcs: triadPcs(scale[i], d.quality),
  }));
}

/* ── the circle of fifths ─────────────────────────────────────────────── */

/**
 * Twelve positions, clockwise from C, each a perfect fifth above the last.
 *
 * The flat side is named A♭, E♭, B♭ — NOT G♯, D♯, A♯. Those would be keys of
 * eight, nine and ten sharps; they are not keys anyone writes, and printing
 * them contradicted the lesson text on five separate pages.
 */
export const CIRCLE_OF_FIFTHS = [
  "C", "G", "D", "A", "E", "B", "F#", "Db", "Ab", "Eb", "Bb", "F",
] as const;

/**
 * The enharmonic twin, shown as a subtitle — but only at the two positions
 * where BOTH spellings are keys a musician actually reads: F♯/G♭ (six sharps
 * or six flats) and D♭/C♯ (five flats or seven sharps). A♭ is not also "G♯
 * major", so nothing is printed there.
 */
export const CIRCLE_ALT: Partial<Record<string, string>> = {
  "F#": "Gb",
  "Db": "C#",
};

/**
 * Position of a key on the circle, tolerant of enharmonic spelling: "Bb",
 * "A#", "B♭" and "a#" all resolve to the same seat. Returns -1 if unknown.
 *
 * Use this instead of `CIRCLE_OF_FIFTHS.indexOf(name)` — a bare indexOf ties
 * every caller to one particular spelling and silently returns -1 (which reads
 * as the last element after a modulo) when that spelling changes.
 */
export function circleIndex(name: string): number {
  const p = parseNote(name);
  if (!p) return -1;
  return CIRCLE_OF_FIFTHS.findIndex((n) => parseNote(n)!.pc === p.pc);
}

/** Display label for a circle position: the key name in ♯/♭ glyphs. */
export function circleLabel(i: number): string {
  return pretty(CIRCLE_OF_FIFTHS[((i % 12) + 12) % 12]);
}

/** Enharmonic subtitle for a circle position, or "" when there isn't a useful one. */
export function circleAltLabel(i: number): string {
  const alt = CIRCLE_ALT[CIRCLE_OF_FIFTHS[((i % 12) + 12) % 12]];
  return alt ? pretty(alt) : "";
}

export { accidental };
